/**
 * Device fingerprinting utility to generate a highly persistent
 * and unique identifier for guest users.
 */

export async function getDeviceFingerprint(): Promise<string> {
  // 1. Try to read from localStorage
  try {
    const cached = localStorage.getItem('_si_dfp');
    if (cached) return cached;
  } catch {}

  // 2. Try to read from cookies
  try {
    const match = document.cookie.match(/(^|;)\s*_si_dfp\s*=\s*([^;]+)/);
    if (match) {
      const val = match[2];
      // Sync back to localStorage
      try { localStorage.setItem('_si_dfp', val); } catch {}
      return val;
    }
  } catch {}

  // 3. Canvas fingerprinting logic
  let canvasDna = '';
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      canvas.width = 200;
      canvas.height = 50;
      // Background / lighting gradient
      const grad = ctx.createLinearGradient(0, 0, 150, 0);
      grad.addColorStop(0, '#aace16');
      grad.addColorStop(1, '#ff007f');
      ctx.fillStyle = grad;
      ctx.fillRect(10, 10, 180, 30);
      
      // Text rendering
      ctx.font = '16px "Arial", sans-serif';
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('SmartImagify DNA ~!@#$', 15, 12);
      
      ctx.shadowColor = 'rgba(0,0,0,0.5)';
      ctx.shadowBlur = 4;
      ctx.fillStyle = '#000000';
      ctx.fillText('SmartImagify DNA ~!@#$', 17, 14);
      
      canvasDna = canvas.toDataURL();
    }
  } catch {}

  // 4. Combine with device hardware characteristics
  const parts: string[] = [
    canvasDna,
    navigator.userAgent || '',
    navigator.language || '',
    (screen.width || 0).toString(),
    (screen.height || 0).toString(),
    (screen.colorDepth || 0).toString(),
    (navigator.hardwareConcurrency || 0).toString(),
    ((navigator as any).deviceMemory || 0).toString(),
    new Date().getTimezoneOffset().toString()
  ];

  // Hash the fingerprint
  const rawString = parts.join('|');
  const hash = await sha256(rawString);

  // 5. Store fingerprint persistently in localStorage & cookie
  try {
    localStorage.setItem('_si_dfp', hash);
  } catch {}

  try {
    const expiry = new Date();
    expiry.setFullYear(expiry.getFullYear() + 10); // 10 years expiry
    document.cookie = `_si_dfp=${hash}; expires=${expiry.toUTCString()}; path=/; SameSite=Lax`;
  } catch {}

  return hash;
}

// Simple SHA-256 hash using Web Crypto API (fast and zero-dependency)
async function sha256(message: string): Promise<string> {
  try {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch {
    // Simple fallback hash if crypto is not supported (e.g. non-secure dev context)
    let hash = 0;
    for (let i = 0; i < message.length; i++) {
      const char = message.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash |= 0;
    }
    return 'fallback_' + Math.abs(hash).toString(16);
  }
}
